import PropTypes from 'prop-types';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import {
    Avatar,
    Box,
    Button,
    Stack,
    SvgIcon,
    TextField,
    Tooltip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress, FormControl, InputLabel
} from '@mui/material';
import { forwardRef, useCallback, useRef, useState } from "react";
import User01Icon from "@untitled-ui/icons-react/build/esm/User01";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "src/libs/firebase";
import {
    RecaptchaVerifier,
    signInWithPhoneNumber,
    updatePhoneNumber,
    PhoneAuthProvider,
    signInWithCredential,
    getAuth
} from "firebase/auth";
import toast from "react-hot-toast";
import { IMaskInput } from 'react-imask';
import { profileApi } from "src/api/profile";
import { formatUSPhoneForDisplay } from 'src/utils/validation/phone';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import { tr } from 'src/locales/tr';
const PhoneMaskInput = forwardRef((props, ref) => {
    const { onChange, ...other } = props;
    return (
        <IMaskInput
            {...other}
            mask="+1 (000) 000-0000"
            definitions={{
                '0': /[0-9]/
            }}
            inputRef={ref}
            onAccept={(value) => onChange({ target: { name: props.name, value } })}
            overwrite
        />
    );
});

PhoneMaskInput.propTypes = {
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};


const AVAILABLE_LANGUAGES = [
    'English',
    'Spanish',
    'Chinese',
    'French',
    'Tagalog',
    'Vietnamese',
    'Arabic',
    'Korean',
    'Russian',
    'German',
    'Ukrainian'
];

export const SpecialistBusinessStep = (props) => {
    const { profile, onNext, step = 1, ...other } = props;
    const [businessName, setBusinessName] = useState(profile.businessName);
    const [phone, setPhone] = useState(formatUSPhoneForDisplay(profile.phone));
    const [fullName, setFullName] = useState(profile.name);
    const [avatar, setAvatar] = useState(profile.avatar || "");
    const [phoneError, setPhoneError] = useState("");
    const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [verificationId, setVerificationId] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const auth = getAuth();
    const recaptchaVerifierRef = useRef(null);
    const [languages, setLanguages] = useState(profile.languages || []);

    const validatePhone = (phoneNumber) => {
        const cleaned = phoneNumber.replace(/\D/g, '');

        if (!cleaned) return ""; // Пустой номер - допустимо

        if (!cleaned.startsWith('1')) {
            return "US phone numbers must start with country code 1";
        }

        if (cleaned.length !== 11) {
            return "US phone number must have 10 digits after country code";
        }

        return "";
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        setPhone(value);
        setPhoneError(validatePhone(value));
    };

    const setupRecaptcha = async () => {
        if (!recaptchaVerifierRef.current) {
            try {
                recaptchaVerifierRef.current = new RecaptchaVerifier(
                    auth,
                    "recaptcha-container",
                    { size: "invisible" }
                );
            } catch (err) {
                console.error("reCAPTCHA initialization error:", err);
                toast.error(tr("Failed to initialize reCAPTCHA. Please reload the page."));
                throw err;
            }
        }
        return recaptchaVerifierRef.current;
    };

    const sendVerificationCode = async () => {
        if (phoneError) {
            toast.error(tr("Please fix phone number errors first"));
            return false;
        }

        if (!phone || phone === "+1 (") {
            // Пользователь не ввел телефон - пропускаем верификацию
            return true;
        }

        try {
            setIsSendingCode(true);
            const appVerifier = await setupRecaptcha();
            const phoneNumber = phone.replace(/\D/g, ''); // Чистим номер для Firebase

            const confirmation = await signInWithPhoneNumber(auth, `+${phoneNumber}`, appVerifier);
            setVerificationId(confirmation.verificationId);
            setVerificationDialogOpen(true);
            return false; // Нужна верификация
        } catch (error) {
            console.error("Error sending verification code:", error);
            toast.error(tr("Failed to send verification code: {{0}}", { 0: error.message }));
            return false;
        } finally {
            setIsSendingCode(false);
        }
    };

    const verifyPhoneNumber = async () => {
        if (!verificationCode) {
            toast.error(tr("Please enter verification code"));
            return false;
        }

        try {
            setIsVerifying(true);
            const credential = PhoneAuthProvider.credential(verificationId, verificationCode);

            // Если пользователь уже аутентифицирован (через Google/Facebook)
            if (auth.currentUser) {
                await updatePhoneNumber(auth.currentUser, credential);

                // Проверяем, совпадает ли email с профилем
                if (auth.currentUser.email === profile.email) {
                    toast.success(tr("Phone number verified and linked to your account!"));
                    return true;
                } else {
                    toast.error(tr("This phone number doesn't match your account email"));
                    return false;
                }
            } else {
                // Если пользователь не аутентифицирован (маловероятно в этом компоненте)
                await signInWithCredential(auth, credential);
                toast.success(tr("Phone number verified!"));
                return true;
            }
        } catch (error) {
            console.error("Error verifying code:", error);
            toast.error(tr("Verification failed: {{0}}", { 0: error.message }));
            return false;
        } finally {
            setIsVerifying(false);
        }
    };

    const handleOnNext = async () => {
        // Проверяем обязательные поля
        if (!businessName || !fullName) {
            toast.error(tr("Please fill all required fields"));
            return;
        }

        // Генерируем profilePage из businessName
        let profilePage = businessName
            .toLowerCase()
            .replace(/[^\w\s]/gi, '') // Удаляем специальные символы
            .replace(/\s+/g, '-') // Заменяем пробелы на дефисы
            .substring(0, 50); // Ограничиваем длину

        // Проверяем уникальность profilePage
        if (profilePage) {
            let counter = 1;
            let originalProfilePage = profilePage;
            let isUnique = false;

            while (!isUnique) {
                try {
                    const exists = await profileApi.checkProfilePageExists(profilePage, profile.id);
                    if (!exists) {
                        isUnique = true;
                    } else {
                        profilePage = `${originalProfilePage}-${counter}`;
                        counter++;
                    }
                } catch (error) {
                    console.error("Error checking profile page uniqueness:", error);
                    toast.error(tr("Error checking profile page availability"));
                    profilePage = profile.id;
                    break;
                }
            }
        }
        const rawDigits = phone?.replace(/\D/g, '') || '';
        let cleanPhone = rawDigits.length > 1 ? `+${rawDigits}` : '';
        // Если телефон не изменился или пустой - пропускаем верификацию
        if (!cleanPhone || cleanPhone === profile.phone) {
            await proceedWithUpdate(cleanPhone, profilePage);
            return;
        }

        // Проверяем существование телефона в базе
        const isPhoneExist = await profileApi.checkExistPhone(cleanPhone, profile.id, profile.email);
        if (isPhoneExist) {
            toast.error(tr("Phone number is already registered"));
            setPhoneError(tr("Phone number is already registered"));
            return;
        }

        // Запускаем процесс верификации
        const canSkipVerification = await sendVerificationCode();
        if (canSkipVerification) {
            await proceedWithUpdate(cleanPhone, profilePage);
        }
    };

    const proceedWithUpdate = async (verifiedPhone = `+${phone?.replace(/\D/g, '')}`, profilePage = profile.profilePage) => {
        if (profile.name === fullName && profile.avatar === avatar &&
            profile.businessName === businessName && profile.phone === verifiedPhone &&
            profile.profilePage === profilePage && JSON.stringify(profile.languages) === JSON.stringify(languages)) {
            onNext();
        } else {
            const changed = {
                name: fullName,
                businessName: businessName,
                avatar: avatar,
                phone: verifiedPhone,
                profilePage: profilePage,
                languages: languages,
                ...(step && { profileDataProgress: step })
            };
            onNext(changed);
        }
    };

    const handleVerifyAndContinue = async () => {
        const isVerified = await verifyPhoneNumber();
        if (isVerified) {
            setVerificationDialogOpen(false);
            await proceedWithUpdate(phone); // Используем текущий номер телефона
        }
    };

    const handleSkipVerification = async () => {
        setVerificationDialogOpen(false);
        setPhone(""); // Сбрасываем телефон, так как он не верифицирован
        await proceedWithUpdate(""); // Продолжаем без телефона
    };

    const fileInputRef = useRef(null);
    const handleAttach = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleAvatarChange = useCallback(async (e) => {
        try {
            if (e.target.files) {
                const file = e.target.files[0];
                const storageRef = ref(storage, '/avatar/' + profile.id + '-' + file.name);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);
                setAvatar(url);
                toast.success(tr("Image uploaded successfully!"));
            }
        } catch (err) {
            console.error(err);
            toast.error(tr("Something went wrong!"));
        }
    }, [setAvatar, profile]);

    return (
        <Stack spacing={3} {...other}>

            <div>
                <Typography variant="h6">{tr("Stand out to customers")}</Typography>
                <Typography variant="body2">
                    {tr("Add a few details to your profile, to help customers get to know you better.")}
                </Typography>
            </div>

            <Tooltip
                title={tr("Enter your first and last name as you would like them to appear in official communications")}>
                <TextField
                    error={!fullName}
                    helperText={!fullName && tr("Required to fill")}
                    fullWidth
                    label={tr("Your full name")}
                    name="fullname"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                />
            </Tooltip>

            <Tooltip title={tr("This is the name that will be displayed to other users on the platform")}>
                <TextField
                    error={!businessName}
                    helperText={!businessName && tr("Required to fill")}
                    fullWidth
                    label={tr("Business name")}
                    name="jobTitle"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                />
            </Tooltip>

            <TextField
                fullWidth
                label={tr("Phone")}
                name="phone"
                value={phone}
                onChange={handlePhoneChange}
                error={!!phoneError}
                helperText={phoneError || tr("Optional, but recommended for business communications")}
                placeholder="+1 (123) 456-7890"
                InputProps={{
                    inputComponent: PhoneMaskInput,
                }}
            />

            <FormControl fullWidth>
                <InputLabel>{tr("Languages")}</InputLabel>
                <Select
                    multiple
                    value={languages}
                    onChange={(e) => setLanguages(e.target.value)}
                    label={tr("Languages")}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                                <Chip
                                    key={value}
                                    label={value}
                                    size="small"
                                    sx={{ height: '24px' }}
                                />
                            ))}
                        </Box>
                    )}
                >
                    {AVAILABLE_LANGUAGES.map((lang) => (
                        <MenuItem key={lang} value={lang}>
                            <Checkbox checked={languages.indexOf(lang) > -1} />
                            <ListItemText primary={lang} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <div>
                <Typography variant="body2">
                    {tr("And add your business logo or personal photo:")}
                </Typography>
                <Alert
                    severity="info"
                    icon={<CameraAltIcon fontSize="small" />}
                    sx={{ mt: 1 }}
                >
                    <AlertTitle sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {tr("Profile Photo Recommendation")}
                    </AlertTitle>
                    {tr("For higher client trust and better visibility, we recommend uploading a")}{' '}<strong>{tr("real profile photo")}</strong>{tr(". Specialists with photos rank higher in search results and attract more bookings.")}
                    <br />
                    <em>{tr("No photo? Your profile may appear below others in search.")}</em>
                </Alert>
            </div>

            <Stack alignItems="center" direction="row" spacing={2}>
                <Box
                    sx={{
                        borderColor: 'neutral.300',
                        borderRadius: '50%',
                        borderStyle: 'dashed',
                        borderWidth: 1,
                        p: '4px'
                    }}
                >
                    <Box
                        sx={{
                            borderRadius: '50%',
                            height: '100%',
                            width: '100%',
                            position: 'relative'
                        }}
                    >
                        <Avatar
                            src={avatar}
                            sx={{
                                height: 100,
                                width: 100
                            }}
                        >
                            <SvgIcon>
                                <User01Icon />
                            </SvgIcon>
                        </Avatar>
                    </Box>
                </Box>
                <Button
                    color="inherit"
                    size="small"
                    onClick={handleAttach}
                >
                    {tr("Change")}
                </Button>
                <input
                    hidden
                    ref={fileInputRef}
                    type="file"
                    onChange={handleAvatarChange}
                />
            </Stack>

            <div id="recaptcha-container"></div>

            <Stack alignItems="center" direction="row" spacing={2}>
                <Button
                    endIcon={(
                        <SvgIcon>
                            <ArrowRightIcon />
                        </SvgIcon>
                    )}
                    onClick={handleOnNext}
                    variant="contained"
                    disabled={!businessName || !fullName || !!phoneError || isSendingCode}
                >
                    {isSendingCode ? <CircularProgress size={24} /> : (!step ? tr("Save") : tr("Next"))}
                </Button>
            </Stack>

            {/* Диалог верификации */}
            <Dialog open={verificationDialogOpen} onClose={() => setVerificationDialogOpen(false)}>
                <DialogTitle>{tr("Verify Phone Number")}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {tr("We've sent a verification code to {{0}}. Please enter it below:", { 0: phone })}
                    </Typography>
                    <TextField
                        fullWidth
                        label={tr("Verification Code")}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        disabled={isVerifying}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSkipVerification} disabled={isVerifying}>
                        {tr("Skip Verification")}
                    </Button>
                    <Button
                        onClick={handleVerifyAndContinue}
                        disabled={isVerifying || !verificationCode}
                        variant="contained"
                    >
                        {isVerifying ? <CircularProgress size={24} /> : tr("Verify & Continue")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

SpecialistBusinessStep.propTypes = {
    onBack: PropTypes.func,
    onNext: PropTypes.func,
    profile: PropTypes.object.isRequired
};